import {
  Bars3Icon,
  ChevronLeftIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { ChevronDoubleLeftIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import Link from 'next/link'
import { FC } from 'react'

import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import AppLogoSvg from '@/assets/svg/AppLogo.svg'
import UserProfile from '@/components/entities/user/ui/UserProfile'

import { Notifications } from '../../header/ui/Notifications'

type Props = {
  className?: string
  isSidebarExpanded: boolean
  toggleSidebar: () => void
  collapseSidebar: () => void
}

const SidebarHead: FC<Props> = ({
  className,
  isSidebarExpanded,
  toggleSidebar,
  collapseSidebar
}) => {
  const { breakpoint } = useCurrentBreakpoint()

  const isMobileScreen = [
    ScreenSize.Xxs,
    ScreenSize.Xs,
    ScreenSize.Sm
  ].includes(breakpoint)

  return (
    <>
      <div
        data-testid="sidebar-head-id"
        className={clsx(
          'flex h-80 justify-between p-26 xs:hidden',
          isSidebarExpanded && 'px-30',
          className
        )}
      >
        {isSidebarExpanded && (
          <Link href="/" data-testid="sidebar-logo-link-id">
            <AppLogoSvg className="text-white transition-colors hover:!text-white/50" />
          </Link>
        )}

        <button
          type="button"
          data-testid="sidebar-toggle-button-id"
          className="group relative h-28 w-28 text-white xxl:flex"
          title={isSidebarExpanded ? 'Свернуть' : 'Раскрыть'}
          onClick={toggleSidebar}
        >
          {isSidebarExpanded ? (
            <>
              <ChevronDoubleLeftIcon
                className={cn(
                  'absolute inset-0 transition-all',
                  'translate-x-5 scale-75 opacity-0',
                  'group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100'
                )}
              />
              <ChevronLeftIcon
                className={cn(
                  'absolute inset-0 transition-all',
                  'group-hover:scale-75 group-hover:opacity-0'
                )}
              />
            </>
          ) : (
            <Bars3Icon
              className={cn(
                'absolute inset-0 transition-all',
                '-translate-x-0 scale-100 xs:-translate-x-5 xs:scale-75 xs:opacity-0',
                'group-hover:translate-x-0 group-hover:scale-100 group-hover:opacity-100'
              )}
            />
          )}
        </button>
      </div>

      <div
        data-testid="sidebar-head-mobile-id"
        className={clsx(
          'hidden items-center justify-between bg-grey-100 p-16 xs:flex',
          className
        )}
      >
        <div className="flex items-center gap-16">
          <button
            aria-label={isSidebarExpanded ? 'Свернуть меню' : 'Раскрыть меню'}
            type="button"
            data-testid="sidebar-mobile-menu-toggle-id"
            className="text-white"
            title={isSidebarExpanded ? 'Свернуть' : 'Раскрыть'}
            onClick={collapseSidebar}
          >
            <XMarkIcon className="h-28 w-28" />
          </button>
          <Link href="/" data-testid="sidebar-logo-link-id">
            <AppLogoSvg className="text-white transition-colors hover:!text-white/50" />
          </Link>
        </div>
        {isSidebarExpanded && isMobileScreen && (
          <div className="flex items-center gap-12">
            <Notifications />
            <UserProfile />
          </div>
        )}
      </div>
    </>
  )
}

export { SidebarHead }
